import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button, Card, Modal, Spinner,
} from '@heroui/react'
// Most icons come from our custom set in ./Icons (24×24 SVGs designed for the
// site). What's still on lucide: ChevronsLeft/Right (double-chevron pagination
// arrows — distinct glyph the user hasn't drawn), X (modal close), and the
// Star outline used inside ./lib's <Star> (no outline variant exists yet —
// the custom set only has Favourite_Filled).
import {
  Activity, AlertTriangle, Blog, ChevronLeft,
  Database, Discord, Search, Server as ServerIcon,
  Settings as SettingsIcon,
  Sprout, TrendingUp,
} from '@dzm/ui/icons'
// Radar is now served by the SVG wordmark logo in /public/logo.svg — drop
// the lucide import. X stays because it's a distinct glyph not in the custom
// set. (ChevronsLeft/Right moved with the server browser into packages/ui.)
import { Menu as MenuIcon, X } from 'lucide-react'
import {
  api, auth as authApi, userApi, fmt, flag,
  type Range, type Overview, type ServerRow,
} from './api'
import { initTracking, trackPageview, trackEvent } from './track'
import { getConsent, isConfigured as analyticsConfigured } from './analytics'
import { estimatePingMs } from './geo'
import { useAuth } from './AuthContext'
import { useCelebrate } from './Celebration'
import { AuthModal } from '@dzm/ui/auth-modal'
// Region radar ("📍 Near me") is paused for now. The component and its data
// (player/src/RegionRadar.tsx + player/src/geo.ts) are kept on disk so the
// feature can come back without rebuilding. Re-import here when ready.
// import RegionRadar from './RegionRadar'
import {
  type FilterOptions,
} from './api'
// Shared helpers + small components (chart palette, Star, RangeTabs,
// VanillaBar, RankInfo, Sparkline, …) live in the design system package so the
// lazy chart chunks below (and the launcher) can use them without pulling
// App.tsx into their bundles.
import {
  tzLabel, METRIC_LABEL, type Metric,
  Star, RangeTabs, chipClass,
} from '@dzm/ui/lib'
// The server browser (search, filters, table, pager) is the design system's
// component now, shared with the launcher. Data access + analytics are injected.
import { ServerBrowser } from '@dzm/ui/server-browser'
import { FeaturedStrip } from '@dzm/ui/featured-strip'
import { SectionHead } from '@dzm/ui/section-head'
import { TrendingStrip, TrendingCard, RegionPicker, useDiscover, type DiscoverTab } from '@dzm/ui/trending-strip'
import { BlogPanel } from '@dzm/ui/blog-panel'
// The manifesto (with its contact letter) and the footer moved to the design
// system so the launcher renders the same sections.
import { Manifesto } from '@dzm/ui/manifesto'
import { SiteFooter } from '@dzm/ui/site-footer'
import { HeroCarousel } from '@dzm/ui/hero-carousel'
import type { UrlFilter } from '@dzm/ui/types'

// Chart-bearing components are code-split: recharts (~300 kB min) only loads
// when one of these actually renders. Each gets a Suspense fallback matching
// the existing skeleton/spinner patterns at its callsite.
const ServerModal = lazy(() => import('./ServerModal'))
const Insights = lazy(() => import('./Insights'))
const PopulationChart = lazy(() => import('./PopulationChart'))
const ProfilePageV2 = lazy(() => import('./ProfilePageV2'))
const SettingsPage = lazy(() => import('./SettingsPage'))
const PublicProfile = lazy(() => import('./PublicProfile'))


/* ------------------------------------------------------------------ Timezone settings */
function detectTz(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}
// Top-nav chip for the Discord bot guide (/bot). Hidden for now — flip to true to
// publish, together with BOT_PAGE_ENABLED in the backend (dashboard/app.py) which
// controls the page's search indexing + its own nav/footer links.
const BOT_NAV_ENABLED = true
// Blog nav. /blog is served by our own CMS (dashboard/blog.py) and 404s for
// anyone who is not a signed-in admin until BLOG_PUBLIC=1 is set. Flip this to
// true in the same change that makes the blog public.
const BLOG_NAV_ENABLED = true

const TZ_KEY = 'dayzmetrics.tz'
function useTimezone() {
  const [tz, setTzState] = useState<string>(() => {
    try { return localStorage.getItem(TZ_KEY) || detectTz() } catch { return detectTz() }
  })
  const setTz = (v: string) => { setTzState(v); try { localStorage.setItem(TZ_KEY, v) } catch { /* */ } }
  return { tz, setTz }
}
function tzList(): string[] {
  try {
    const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf
    if (f) return f('timeZone')
  } catch { /* */ }
  return ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Berlin', 'Europe/Moscow', 'Asia/Dubai', 'Asia/Kolkata',
    'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney']
}

/* ------------------------------------------------------------------ Favourites
 * Anonymous: favourites live in localStorage (as before). Signed in: the account's
 * set is the source of truth (server-side, shared with the launcher). On first
 * login we offer to merge any local favourites up to the account once, then
 * follow the account: every toggle is pushed at once, and the set is pulled on
 * load and whenever the tab comes back into view, so a star made in the
 * launcher shows here without a reload. */
const FAVS_KEY = 'dayzmetrics.favourites.v1'
const readLocalFavs = (): number[] => {
  try {
    const v = JSON.parse(localStorage.getItem(FAVS_KEY) || '[]')
    // Defend against a corrupted/hand-edited value: only keep real numbers, so a
    // bad payload can't poison favourites or the favMerge POST body.
    return Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x)) : []
  } catch { return [] }
}
const writeLocalFavs = (ids: number[]) => {
  try { localStorage.setItem(FAVS_KEY, JSON.stringify(ids)) } catch { /* quota / private mode */ }
}
const MERGE_DECLINED_KEY = 'dzm_favmerge_declined'
const mergeDeclined = () => { try { return localStorage.getItem(MERGE_DECLINED_KEY) === '1' } catch { return false } }
// The signed-in account's set, cached per account: a returning visitor sees it on
// first paint and while offline. Never shown for another account, never signed out.
const ACCOUNT_CACHE_KEY = 'dayzmetrics.favourites.account.v1'
const readAccountCache = (uid: number): number[] | null => {
  try {
    const v = JSON.parse(localStorage.getItem(ACCOUNT_CACHE_KEY) || 'null') as { uid?: unknown; ids?: unknown } | null
    if (!v || v.uid !== uid || !Array.isArray(v.ids)) return null
    return v.ids.filter((x): x is number => typeof x === 'number' && Number.isFinite(x))
  } catch { return null }
}
const writeAccountCache = (uid: number, ids: number[]) => {
  try { localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify({ uid, ids })) } catch { /* quota / private mode */ }
}
// focus and visibilitychange fire together; one pull is enough.
const FAV_PULL_THROTTLE_MS = 2000

function useFavourites() {
  const { user } = useAuth()
  const uid = user?.id ?? null
  const [favs, setFavsState] = useState<number[]>(() => readLocalFavs())
  // The current set, readable synchronously by toggle() so its side effects
  // (counters, cache, the API call) run once, outside the state updater.
  const favsRef = useRef(favs)
  const setFavs = useCallback((ids: number[]) => { favsRef.current = ids; setFavsState(ids) }, [])
  // Local favourites the signed-in account doesn't have yet, awaiting the user's
  // explicit decision in FavMergePrompt. We deliberately DON'T merge silently — the
  // old auto-merge ran during the OAuth redirect and could fail unseen, losing them.
  const [pendingMerge, setPendingMerge] = useState<number[]>([])
  const loadedFor = useRef<number | null>(null)
  const uidRef = useRef<number | null>(null)
  const inFlight = useRef(0)   // toggles the API has not answered yet
  const seq = useRef(0)        // bumps on every toggle; a pull that raced one is dropped
  // Adopt the account's set. Skipped while a toggle is in flight, and dropped if
  // one happened while we waited, so an optimistic star is never clobbered (the
  // next focus reconciles). The first pull per account also offers to bring over
  // local favourites the account doesn't have.
  const pull = useCallback(async (id: number) => {
    if (inFlight.current > 0) return
    const start = seq.current
    const first = loadedFor.current !== id
    const local = readLocalFavs()
    try {
      const r = await userApi.favourites()
      if (uidRef.current !== id || inFlight.current > 0 || seq.current !== start) return
      loadedFor.current = id
      setFavs(r.ids)
      writeAccountCache(id, r.ids)
      if (first) {
        const extra = local.filter((x) => !r.ids.includes(x))
        if (extra.length && !mergeDeclined()) setPendingMerge(extra)
      }
    } catch { /* offline: keep what is shown */ }
  }, [setFavs])
  useEffect(() => {
    uidRef.current = uid
    if (uid === null) { loadedFor.current = null; setPendingMerge([]); setFavs(readLocalFavs()); return }
    // Keyed on the account id, not the `user` object: a refresh() must not
    // refetch and clobber optimistic toggles. Paint the cached set at once,
    // then ask the account, and ask again whenever the tab comes back.
    if (loadedFor.current !== uid) {
      const cached = readAccountCache(uid)
      if (cached) setFavs(cached)
      pull(uid)
    }
    let last = Date.now()
    const onFocus = () => {
      if (document.hidden) return
      const now = Date.now()
      if (now - last < FAV_PULL_THROTTLE_MS) return
      last = now
      pull(uid)
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [uid, pull, setFavs])
  const toggle = useCallback((id: number) => {
    const cur = favsRef.current
    const adding = !cur.includes(id)
    const next = adding ? [...cur, id] : cur.filter((x) => x !== id)
    setFavs(next)
    if (uid !== null) {
      seq.current++
      inFlight.current++
      writeAccountCache(uid, next)
      ;(adding ? userApi.favAdd(id) : userApi.favRemove(id)).catch(() => {}).finally(() => { inFlight.current-- })
    } else { writeLocalFavs(next) }
    trackEvent('favourite', id, adding ? 'add' : 'remove')
  }, [uid, setFavs])
  // Confirm the merge prompt: push local favourites onto the account, then drop the
  // (now-redundant) local copy. Returns false on failure so the prompt can show an error.
  const acceptMerge = useCallback(async (): Promise<boolean> => {
    try {
      const r = await userApi.favMerge(readLocalFavs())
      setFavs(r.ids)
      if (uid !== null) writeAccountCache(uid, r.ids)
      writeLocalFavs([])
      setPendingMerge([])
      return true
    } catch { return false }
  }, [uid, setFavs])
  const declineMerge = useCallback(() => {
    try { localStorage.setItem(MERGE_DECLINED_KEY, '1') } catch { /* private mode */ }
    setPendingMerge([])
  }, [])
  return { favs, toggle, pendingMerge, acceptMerge, declineMerge }
}

// Nudge logged-out visitors who've saved servers (in localStorage) to sign in, so
// their favourites get merged onto an account and survive across devices. The merge
// itself happens automatically on first login (useFavourites → favMerge); this just
// surfaces the prompt. Dismissal persists; the merge still works if they sign in later.
const FAVNUDGE_KEY = 'dzm_favnudge_dismissed'
function FavMergeNudge({ count, onSignIn }: { count: number; onSignIn: () => void }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(FAVNUDGE_KEY) === '1' } catch { return false }
  })
  if (dismissed) return null
  // Matches BetaNotice's styling: subtle bordered box, brand icon, mono label, muted
  // body — with the Sign in + dismiss actions appended on the right.
  return (
    <div role="status"
      className="mb-3 rounded-xl border border-[var(--border)] bg-[var(--background-secondary)]
                 px-3.5 py-2.5 flex items-center gap-2.5">
      <Star size={18} fill="currentColor" className="shrink-0 text-[var(--brand)]" />
      <div className="text-[12px] leading-snug text-[var(--muted)] flex-1 min-w-0">
        <span className="text-[var(--foreground)] mono uppercase tracking-wider text-[10px] mr-2 align-middle">Saved</span>
        <span className="align-middle">
          You have <span className="text-[var(--foreground)]">{count}</span> saved {count === 1 ? 'server' : 'servers'} on
          this device. Sign in to keep them on your account, on any device.
        </span>
      </div>
      <button type="button" onClick={onSignIn} className={`${chipClass(true)} h-8 px-3.5 shrink-0`}>Sign in</button>
      <button type="button" aria-label="Dismiss"
        onClick={() => { setDismissed(true); try { localStorage.setItem(FAVNUDGE_KEY, '1') } catch { /* private mode */ } }}
        className="shrink-0 text-[var(--muted)] hover:text-[var(--foreground)] p-1 -mr-1">
        <X size={16} />
      </button>
    </div>
  )
}

// Post-sign-in prompt: offer to move favourites saved on this device (logged out)
// onto the account. Explicit Yes/No so the merge happens on a deliberate click with
// the session fully established — and the user knows the local copy is cleared.
function FavMergePrompt({ count, onAdd, onDecline }: { count: number; onAdd: () => Promise<boolean>; onDecline: () => void }) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const noun = count === 1 ? 'server' : 'servers'
  const them = count === 1 ? 'it' : 'them'
  return (
    <Modal.Backdrop isOpen onOpenChange={(o) => { if (!o && !busy) onDecline() }}>
      <Modal.Container>
        <Modal.Dialog className="max-w-[420px] w-full">
          <Modal.Header><Modal.Heading className="text-lg">Add your saved servers?</Modal.Heading></Modal.Header>
          <Modal.Body className="space-y-3.5">
            <p className="text-[14px] text-[var(--muted)] leading-relaxed">
              You favourited <span className="text-[var(--foreground)] font-medium">{count}</span> {noun} on this device
              before signing in. Add {them} to your account so your favourites stay with you on every device.
            </p>
            <p className="text-[12px] text-[var(--muted)] leading-relaxed">
              Once added, {them === 'it' ? "it's" : "they're"} saved to your account and the copy on this device is cleared.
            </p>
            {failed && <p className="text-[13px] text-red-400">Couldn’t add {them} just now — check your connection and try again.</p>}
            <div className="flex items-center gap-3 pt-1">
              <Button className="flex-1" isDisabled={busy}
                onPress={async () => { setBusy(true); setFailed(false); const ok = await onAdd(); if (!ok) { setFailed(true); setBusy(false) } }}>
                {busy ? 'Adding…' : `Add ${count} to my account`}
              </Button>
              <button type="button" disabled={busy} onClick={onDecline}
                className="mono text-xs px-3 py-2 rounded-lg text-[var(--muted)] hover:text-[var(--foreground)] disabled:opacity-50 transition-colors shrink-0">
                No thanks
              </button>
            </div>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

/* ------------------------------------------------------------------ KPI */
// `hero` is kept in the prop type as a no-op so callers don't break, but the
// active-state design doesn't differentiate hero vs non-hero anymore — both
// render the same. Underscore-prefixed to silence the unused-var lint.
function Kpi({ label, value, sub, hero: _hero, onClick, active }: {
  label: string; value: string; sub?: string; hero?: boolean
  onClick?: () => void; active?: boolean
}) {
  // Active state = "label brand colour" variation (kpi-test #9): the label
  // turns brand-orange, the card lifts to background-tertiary, the value
  // stays white. No ring, no dot, no glow — the quietest brand cue while
  // still being clearly the picked card.
  const card = (
    <Card variant="default" className={`h-full transition-colors ${active ? 'bg-[var(--background-tertiary)] [--icon-accent:var(--brand)]' : ''} ${onClick ? 'hover:bg-[var(--surface-1-hover)]' : ''}`}>
      <Card.Content className="py-4">
        <div className={`mono text-[10px] uppercase tracking-wider mb-2 ${active ? 'text-[var(--brand)]' : 'text-[var(--muted)]'}`}>
          {label}
        </div>
        <div className="mono text-3xl leading-none text-[var(--foreground)]">{value}</div>
        {sub && <div className="mono text-[11px] text-[var(--muted)] mt-2">{sub}</div>}
      </Card.Content>
    </Card>
  )
  return onClick
    ? <button type="button" onClick={onClick} className="text-left w-full cursor-pointer" aria-pressed={active}>{card}</button>
    : card
}

/* PopulationChart, Heatmap, RestartCard, SunCycle, NoticeBanner, ServerModal,
   KV, CopyField, ModsSection live in ./ServerModal.tsx + ./PopulationChart.tsx
   (lazy-loaded); shared bits (VanillaBar, RankInfo, RangeTabs, …) in ./lib. */


// Slim full-width strip pinned above the hero. Set the right expectation about
// the platform being early without stealing attention from the content below.
function BetaNotice() {
  return (
    <div role="status"
      className="mb-3 rounded-xl border border-[var(--border)] bg-[var(--background-secondary)]
                 px-3.5 py-2.5 flex items-center gap-2.5">
      <AlertTriangle size={18} className="shrink-0 text-[var(--brand)]" />
      <div className="text-[12px] leading-snug text-[var(--muted)]">
        <span className="text-[var(--foreground)] mono uppercase tracking-wider text-[10px] mr-2 align-middle">Beta</span>
        <span className="align-middle">
          dayzmetrics is in active development. You'll see new features, layout changes and the
          occasional bug while we build. Some real servers may not show in the browser yet &mdash;
          our fake-detection is still sharpening, and we are tuning it to hide the farms without
          hiding genuine communities. Spot a missing server or anything off? Use the contact form
          at the bottom of the page. Every report helps.
        </span>
      </div>
    </div>
  )
}


/* Insights (Maps & mods trends) lives in ./Insights.tsx — lazy-loaded so
   recharts stays out of the main bundle. */

/* ------------------------------------------------------------------ Section heading */
/* SectionHead moved to packages/ui (section-head.tsx); shared with the launcher. */

/* ------------------------------------------------------------------ Discover full-page */
// Dedicated /trending and /upcoming pages. Same card + tab toggle as the home
// strip, but renders ALL items in a grid and has its own header with a "← Back"
// button that pops the URL back to /.
function DiscoverPage({ tab, setTab, onOpen, onClose, region, you, viewerCC, countries, onRegionChange }: {
  tab: DiscoverTab; setTab: (t: DiscoverTab) => void
  onOpen: (id: number) => void; onClose: () => void
  region: string | null
  you: string | null
  viewerCC: string | null
  countries: { code: string; count: number }[]
  onRegionChange: (cc: string | null) => void
}) {
  const data = useDiscover(api, tab, region)
  const all = data ?? []
  const loading = data === null
  const title = tab === 'trending' ? 'Trending now' : 'New & upcoming'
  const subtitle = tab === 'trending'
    ? 'quality-gated · momentum over the last 24h'
    : 'recently discovered · showing real growth · not yet ranked'
  return (
    <main className="max-w-[1320px] mx-auto px-5 py-6">
      <button type="button" onClick={onClose}
        className="mono text-xs uppercase tracking-wider rounded-full px-3 py-1.5 bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] inline-flex items-center gap-1.5 mb-4">
        <ChevronLeft size={16} /> Back to browser
      </button>
      <div className="mb-3 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            {tab === 'trending' ? <TrendingUp size={16} className="text-[var(--accent)]" /> : <Sprout size={16} className="text-[var(--accent)]" />}
            {title}
          </h1>
          <p className="mono text-[11px] text-[var(--muted)] mt-1 inline-flex items-center gap-1.5 flex-wrap">
            <span>Servers</span>
            <RegionPicker region={region} you={you} countries={countries} onChange={onRegionChange} />
            <span className="opacity-50">·</span>
            <span>{subtitle}</span>
            <span className="opacity-50">·</span>
            <span className="text-[var(--foreground)]">{all.length}</span><span>shown</span>
          </p>
        </div>
        {/* Tab toggle so you can flip categories without going back. */}
        <div className="inline-flex rounded-full bg-[var(--surface-1)] p-1 gap-0.5">
          <button type="button" onClick={() => setTab('trending')}
            className={`mono text-[11px] uppercase tracking-wider px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors
              ${tab === 'trending' ? 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]' : 'text-[var(--muted)] hover:text-[var(--foreground)]'}`}>
            <TrendingUp size={16} /> Trending now
          </button>
          <button type="button" onClick={() => setTab('upcoming')}
            className={`mono text-[11px] uppercase tracking-wider px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors
              ${tab === 'upcoming' ? 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]' : 'text-[var(--muted)] hover:text-[var(--foreground)]'}`}>
            <Sprout size={16} /> New &amp; upcoming
          </button>
        </div>
      </div>
      {loading
        ? <div className="mono text-[12px] text-[var(--muted)] py-6 px-4 rounded-xl bg-[var(--background-secondary)]">Loading…</div>
        : all.length === 0
        ? <div className="mono text-[12px] text-[var(--muted)] py-6 px-4 rounded-xl bg-[var(--background-secondary)] flex items-center gap-3 flex-wrap">
            <span>
              {region
                ? <>No {tab === 'trending' ? 'trending' : 'new'} servers in <span className="text-[var(--foreground)]">{flag(region)} {region}</span> right now.</>
                : (tab === 'trending'
                    ? 'No servers with momentum right now. Check back soon.'
                    : 'No new servers in the last 30 days with real activity yet.')}
            </span>
            {region && (
              <button type="button" onClick={() => onRegionChange(null)}
                className="mono text-[11px] uppercase tracking-wider rounded-full px-3 py-1.5 bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] transition-colors">
                Try 🌍 Anywhere
              </button>
            )}
          </div>
        : (
          <div key={tab} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {all.map((it, i) => <TrendingCard key={it.id} item={it} onOpen={onOpen} viewerCC={viewerCC} index={i} estimatePing={estimatePingMs} />)}
          </div>
        )}
    </main>
  )
}

/* FeaturedStrip moved to packages/ui (featured-strip.tsx); shared with the launcher. */

/* ------------------------------------------------------------------ Settings */
function SettingsModal({ tz, setTz, onClose }: { tz: string; setTz: (v: string) => void; onClose: () => void }) {
  const zones = tzList()
  return (
    <Modal.Backdrop isOpen onOpenChange={(o) => !o && onClose()}>
      <Modal.Container>
        <Modal.Dialog className="max-w-[420px] w-full">
          <Modal.Header><Modal.Heading className="text-lg">Settings</Modal.Heading></Modal.Header>
          <Modal.Body className="space-y-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1.5">Your timezone</div>
              <select value={tz} onChange={(e) => setTz(e.target.value)} className="field-input px-3 w-full" aria-label="Timezone">
                {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
              </select>
              <p className="mono text-[11px] text-[var(--muted)] mt-2 leading-relaxed">
                Busiest-times and population charts are shown in <span className="text-[var(--foreground)]">{tzLabel(tz)}</span> ({tz.replace(/_/g, ' ')}).
              </p>
            </div>
            {tz === detectTz()
              ? <p className="mono text-[11px] text-[var(--muted)]">Already using your device timezone.</p>
              : <Button size="sm" variant="secondary" onPress={() => setTz(detectTz())}>Use device timezone</Button>}
          </Modal.Body>
          <Modal.Footer><Button slot="close" variant="secondary">Done</Button></Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

/* ------------------------------------------------------------------ Top bar */
// Floating panel that matches the rest of the page cards (same gray fill,
// same rounded corners, same content-area width as <main>). No edge-to-edge
// chrome and no backdrop strip behind it — the bar reads as part of the
// page, not a UI strip stuck on top.
//
// It also hides on scroll-down past a small threshold and slides back in on
// scroll-up, so it stops getting in the way once the user starts reading.
// The motion is a CSS transform transition (no rAF gymnastics on the styles)
// so the GPU does the work and `prefers-reduced-motion` can flatten it via
// the existing card-rise-in rule if we ever opt in.
/* Pending invite code (stored by /invite/<code> or ?ref=), attributed to the
 * next sign-up through the auth modal. */
const readRef = () => { try { return localStorage.getItem('dzm_ref') || null } catch { return null } }

/* Top-bar account control: a "Sign in" button when logged out, or an avatar
 * that opens a small account menu when signed in. (Profile link arrives with
 * the /profile page in Phase 2.) */
function AuthControl({ openAuth, openProfile }: { openAuth: () => void; openProfile: () => void }) {
  const { user, loading, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [resent, setResent] = useState(false)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])
  if (loading) return null
  if (!user) {
    return (
      <button type="button" onClick={openAuth}
        className="mono text-xs uppercase tracking-wider rounded-full bg-[var(--brand-soft)] text-[var(--brand)] ring-1 ring-[var(--brand)] inline-flex items-center gap-2 px-3.5 py-2 whitespace-nowrap transition hover:brightness-110">
        Sign in
      </button>
    )
  }
  const initial = (user.display_name || user.email || '?').trim().charAt(0).toUpperCase()
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="true" aria-expanded={open}
        aria-label="Account"
        className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full overflow-hidden bg-[var(--background-tertiary)] ring-1 ring-[var(--border)] hover:ring-[var(--muted)] transition">
        {user.avatar_url
          ? <img src={user.avatar_url} alt="" width={36} height={36} className="w-9 h-9 object-cover" />
          : <span className="mono text-sm font-semibold text-[var(--foreground)]">{initial}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div role="menu"
            className="absolute right-0 top-full mt-2 z-50 min-w-[240px] overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--background-secondary)] shadow-xl py-1.5">
            <div className="px-4 py-2.5 border-b border-[var(--border)]">
              <div className="text-sm font-medium truncate">{user.display_name || 'Survivor'}</div>
              {user.email && <div className="mono text-[11px] text-[var(--muted)] truncate">{user.email}</div>}
            </div>
            {user.email && !user.email_verified && (
              <button type="button" role="menuitem"
                onClick={async () => { try { await authApi.resendVerify(); setResent(true) } catch { /* ignore */ } }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--brand)] hover:bg-[var(--background-tertiary)] transition-colors">
                {resent ? 'Verification email sent ✓' : 'Verify your email'}
              </button>
            )}
            <button type="button" role="menuitem"
              onClick={() => { setOpen(false); openProfile() }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
              Profile
            </button>
            <button type="button" role="menuitem"
              onClick={async () => { setOpen(false); await logout() }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
              Log out
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function TopBar({
  scrollToBrowser, openSettings, openAuth, openProfile,
}: { scrollToBrowser: () => void; openSettings: () => void; openAuth: () => void; openProfile: () => void }) {
  const { user, logout } = useAuth()
  const [hidden, setHidden] = useState(false)
  // Mobile-only actions menu. On narrow phones the logo + four action buttons
  // overflow, so below `sm` they collapse behind a single menu button.
  const [menuOpen, setMenuOpen] = useState(false)
  useEffect(() => { if (hidden) setMenuOpen(false) }, [hidden])  // close on scroll-away
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])
  useEffect(() => {
    let lastY = window.scrollY
    let raf = 0
    const update = () => {
      raf = 0
      const y = window.scrollY
      // Hides aggressively as soon as the user starts scrolling down (past
      // ~24px), and pops back as soon as they scroll up at all. Tight
      // hysteresis (6px each way) keeps it from flickering on touchpad
      // momentum or rubber-band overscroll.
      if (y <= 8) setHidden(false)
      else if (y > 24 && y > lastY + 6) setHidden(true)
      else if (y < lastY - 6) setHidden(false)
      lastY = y
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update) }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf) }
  }, [])
  return (
    // Pure-opacity fade rather than a slide. The bar stays in place and just
    // vanishes — feels less like a navigation chrome being yanked away and
    // more like the page reclaiming focus. Pointer events get disabled while
    // it's invisible so clicks pass through to whatever's underneath.
    <div
      className={`sticky top-0 z-30 pt-3 pb-2 transition-opacity duration-200 ease-out ${
        hidden ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}>
      {/* Inner wrapper matches <main>'s padding so the gray card aligns
          exactly with the content cards below it. */}
      <div className="max-w-[1320px] mx-auto px-5">
        <header className="h-16 px-4 sm:px-5 rounded-2xl flex items-center gap-4"
          style={{ background: 'var(--background-secondary)' }}>
          {/* Brand mark + wordmark as a single SVG asset (mark + "dayz metrics"
              wordmark exported together). Source 276×55, rendered at 40px tall
              so it sits with proper presence inside the 64px top bar. width:auto
              preserves the 5:1 aspect. */}
          <a href="/" className="shrink-0 inline-flex items-center" aria-label="dayzmetrics, home">
            <img src="/logo.svg" alt="dayzmetrics" width={200} height={40}
              style={{ height: 40, width: 'auto', display: 'block' }} />
          </a>
          {/* The "New player?" entry point lived here, but the beginner guide
              is currently offline (gated behind START_PAGE_ENABLED on the
              backend). Re-add this <a href="/start" ...> chip when we flip
              the flag and ship the guide. */}
          {/* Desktop actions: full row of buttons (sm and up). */}
          <div className="ml-auto hidden sm:flex items-center gap-4">
            <button type="button" onClick={scrollToBrowser} aria-label="Search servers"
              className="mono text-xs uppercase tracking-wider rounded-full bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] inline-flex items-center gap-2 px-3.5 py-2 whitespace-nowrap transition-colors">
              <Search size={16} noFloor /> <span>Search servers</span>
            </button>
            {/* Blog. Full-navigation <a> because /blog is rendered by Payload,
                not a React route in this bundle. */}
            {BLOG_NAV_ENABLED && (
            <a href="/blog" aria-label="dayzmetrics blog"
              className="mono text-xs uppercase tracking-wider rounded-full bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] inline-flex items-center gap-2 px-3.5 py-2 whitespace-nowrap transition-colors">
              <Blog size={16} /> <span>Blog</span>
            </a>
            )}
            {/* Discord bot page link. Full-navigation <a> because /bot is a
                server-rendered page, not a React route. */}
            {BOT_NAV_ENABLED && (
              <a href="/bot" aria-label="DayZ Discord bot"
                className="mono text-xs uppercase tracking-wider rounded-full bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] inline-flex items-center gap-2 px-3.5 py-2 whitespace-nowrap transition-colors">
                <Discord size={16} noFloor /> <span>Discord bot</span>
              </a>
            )}
            <button type="button" onClick={openSettings} aria-label="Settings" title="Settings"
              className="rounded-full w-9 h-9 inline-flex items-center justify-center bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] transition-colors">
              <SettingsIcon size={18} noFloor />
            </button>
            <AuthControl openAuth={openAuth} openProfile={openProfile} />
          </div>
          {/* Mobile actions: a single menu button that opens a dropdown (below sm). */}
          <div className="ml-auto sm:hidden relative">
            <button type="button" onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menu" aria-haspopup="true" aria-expanded={menuOpen}
              className="rounded-full p-2 bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)] transition-colors">
              {menuOpen ? <X size={18} /> : <MenuIcon size={18} />}
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden />
                <div role="menu"
                  className="absolute right-0 top-full mt-2 z-50 min-w-[208px] overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--background-secondary)] shadow-xl py-1.5">
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); scrollToBrowser() }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
                    <Search size={16} noFloor /> Search servers
                  </button>
                  {BLOG_NAV_ENABLED && (
                  <a href="/blog" role="menuitem" onClick={() => setMenuOpen(false)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
                    <Blog size={16} /> Blog
                  </a>
                  )}
                  {BOT_NAV_ENABLED && (
                    <a href="/bot" role="menuitem" onClick={() => setMenuOpen(false)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
                      <Discord size={16} noFloor /> Discord bot
                    </a>
                  )}
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); openSettings() }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
                    <SettingsIcon size={16} noFloor /> Settings
                  </button>
                  <div className="my-1 h-px bg-[var(--border)]" />
                  {user ? (
                    <>
                      <div className="px-4 py-2">
                        <div className="text-sm font-medium truncate">{user.display_name || 'Survivor'}</div>
                        {user.email && <div className="mono text-[11px] text-[var(--muted)] truncate">{user.email}</div>}
                      </div>
                      <button type="button" role="menuitem"
                        onClick={() => { setMenuOpen(false); openProfile() }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
                        Profile
                      </button>
                      <button type="button" role="menuitem"
                        onClick={async () => { setMenuOpen(false); await logout() }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors">
                        Log out
                      </button>
                    </>
                  ) : (
                    <button type="button" role="menuitem"
                      onClick={() => { setMenuOpen(false); openAuth() }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--brand)] hover:bg-[var(--background-tertiary)] transition-colors">
                      Sign in
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </header>
      </div>
    </div>
  )
}



// SPA-side recognition of the SSR-only landing URLs (/mod, /map, /servers/{country}).
// Returning null means "this is the homepage or some other route" — the App
// keeps its existing behaviour. Anything else gets fed into the ServerBrowser as
// an initial filter + a contextual header above the browser.

// decodeURIComponent throws URIError on malformed percent-encoding (e.g. `%E0`,
// `%ZZ`); since these run in render/initializers, an unguarded throw white-screens
// the whole SPA for anyone who follows such a link. Fall back to the raw segment.
function dec(s: string): string {
  try { return decodeURIComponent(s) } catch { return s }
}

export function parseUrlFilter(path: string): UrlFilter | null {
  const m1 = path.match(/^\/mod\/(\d+)(?:\/([^/]+))?\/?$/)
  if (m1) return { kind: 'mod', id: Number(m1[1]), slug: m1[2] }
  const m2 = path.match(/^\/map\/([^/]+)\/?$/)
  if (m2) return { kind: 'map', map: dec(m2[1]).toLowerCase() }
  const m3 = path.match(/^\/servers\/([^/]+)\/?$/)
  if (m3) return { kind: 'country', slug: dec(m3[1]).toLowerCase() }
  return null
}

/* ------------------------------------------------------------------ Player app */
// True once the head-start prefetch batch (window.__early, fired from
// index.html before the bundle even loaded) has fully settled — i.e. all the
// data the first paint depends on is in. Used to reveal the below-hero content
// in a single commit instead of section-by-section pop-in (CLS). The timer is
// a hard failsafe: whatever happens to those promises, the page reveals within
// 2.5s. When the prefetch isn't there (dev server, tests), boots immediately.
function useBooted(): boolean {
  const [booted, setBooted] = useState(() => {
    // __earlyAll is snapshotted in index.html at prefetch time — entries are
    // deleted from __early as api.ts consumes them, so only the snapshot can
    // be awaited reliably. No snapshot (dev server, tests) = boot immediately.
    return !(window as { __earlyAll?: Promise<unknown> }).__earlyAll
  })
  useEffect(() => {
    if (booted) return
    let done = false
    const reveal = () => { if (!done) { done = true; setBooted(true) } }
    const all = (window as { __earlyAll?: Promise<unknown> }).__earlyAll
    // allSettled resolves before the strips' own then-chains have committed
    // their state (their chains run extra microtask hops through j()), so an
    // immediate reveal paints one commit early and the pop-in returns. One
    // short macrotask lets those commits land; the page reveals complete.
    if (all) all.then(() => window.setTimeout(reveal, 80)); else reveal()
    const t = window.setTimeout(reveal, 2500)
    return () => window.clearTimeout(t)
  }, [booted])
  return booted
}

export default function App() {
  const booted = useBooted()
  const [ov, setOv] = useState<Overview | null>(null)
  const [range, setRange] = useState<Range>('1d')
  const [metric, setMetric] = useState<Metric>('players')
  const [opts, setOpts] = useState<FilterOptions | null>(null)
  // Deep-linkable server modal: /server/{id} opens that server (matches the SSR pages).
  const [sel, setSel] = useState<number | null>(() => {
    const m = window.location.pathname.match(/^\/server\/(\d+)/)
    return m ? Number(m[1]) : null
  })
  // Deep-linkable Trending / New & upcoming pages: /trending or /upcoming.
  const [discover, setDiscoverState] = useState<'trending' | 'upcoming' | null>(() => {
    const p = window.location.pathname
    if (p === '/trending') return 'trending'
    if (p === '/upcoming') return 'upcoming'
    return null
  })
  // Deep-linkable filter URLs that Google has indexed thousands of:
  //   /mod/{id}/{slug?}       — filter the server browser by mod
  //   /map/{mapname}          — filter by map
  //   /servers/{country-slug} — filter by country
  // The SPA reads the URL on mount, seeds the browser filter, and shows a
  // contextual header so the page delivers what the URL promises (instead of
  // briefly showing SSR content and then overwriting it with the homepage).
  const [urlFilter, setUrlFilter] = useState<UrlFilter | null>(() => parseUrlFilter(window.location.pathname))
  const { favs, toggle: toggleFav, pendingMerge, acceptMerge, declineMerge } = useFavourites()
  const { user: authUser, loading: authLoading, refresh: refreshAuth } = useAuth()
  const celebrate = useCelebrate()
  const { tz, setTz } = useTimezone()
  const [showSettings, setShowSettings] = useState(false)
  // Deep-linkable /profile + /profile/settings pages (auth-gated inside them).
  const [profileOpen, setProfileOpen] = useState(() => window.location.pathname === '/profile')
  const [settingsPageOpen, setSettingsPageOpen] = useState(() => window.location.pathname === '/profile/settings')
  const openProfile = useCallback(() => {
    setSettingsPageOpen(false); setProfileOpen(true)
    window.history.pushState({ profile: 1 }, '', '/profile')
    window.scrollTo(0, 0); trackPageview()
  }, [])
  const openSettingsPage = useCallback(() => {
    setProfileOpen(false); setSettingsPageOpen(true)
    window.history.pushState({ settings: 1 }, '', '/profile/settings')
    window.scrollTo(0, 0); trackPageview()
  }, [])
  const closeProfile = useCallback(() => {
    setProfileOpen(false); setSettingsPageOpen(false)
    if (window.location.pathname.startsWith('/profile')) window.history.pushState({}, '', '/')
  }, [])
  // Public profile /u/<handle> (no auth needed).
  const [publicHandle, setPublicHandle] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/u\/([^/]+)/)
    return m ? dec(m[1]) : null
  })
  const closePublic = useCallback(() => {
    setPublicHandle(null)
    if (window.location.pathname.startsWith('/u/')) window.history.pushState({}, '', '/')
  }, [])
  // On login, merge any anonymous vote cast before signing in into the account.
  const claimedRef = useRef(false)
  useEffect(() => {
    if (authUser && !claimedRef.current) {
      claimedRef.current = true
      // Merging pre-login votes can cross an achievement threshold → celebrate it,
      // and surface the merge as a toast so it isn't silent.
      userApi.claimVotes().then((r) => {
        if (r.unlocked?.length) celebrate(r.unlocked)
        if (r.merged > 0) setVerifyMsg(`Added ${r.merged} rating${r.merged === 1 ? '' : 's'} you made before signing in.`)
      }).catch(() => {})
    }
    if (!authUser) claimedRef.current = false
  }, [authUser, celebrate])
  // Don't strand a signed-out user on /profile or /profile/settings — whether
  // they just logged out (from any button) or opened the link logged out, send
  // them home rather than the empty "not signed in" page.
  useEffect(() => {
    if (!authLoading && !authUser && (profileOpen || settingsPageOpen)) {
      setProfileOpen(false); setSettingsPageOpen(false)
      if (window.location.pathname.startsWith('/profile')) window.history.replaceState({}, '', '/')
    }
  }, [authLoading, authUser, profileOpen, settingsPageOpen])
  // Auth modal. Opens on demand, or automatically when the page loads with a
  // password-reset token (?reset=…). ?verify=ok|invalid shows a one-shot toast.
  const [authModal, setAuthModal] = useState<{ mode: 'signin' | 'signup' | 'forgot' | 'reset'; token?: string } | null>(() => {
    const token = new URLSearchParams(window.location.search).get('reset')
    return token ? { mode: 'reset', token } : null
  })
  const openAuth = useCallback(() => setAuthModal({ mode: 'signin' }), [])
  // Referral capture: /invite/<code> stores the code so a subsequent sign-up
  // attaches the referral, cleans the URL, then nudges sign-up once auth resolves.
  const [pendingInvite, setPendingInvite] = useState<string | null>(() => {
    // Two entry points: the legacy /invite/<code> path, and ?ref=<code> from the
    // server-rendered invite landing page's "Join" button.
    const fromPath = window.location.pathname.match(/^\/invite\/([A-Za-z0-9]+)/)?.[1]
    const fromQuery = new URLSearchParams(window.location.search).get('ref')
    const code = (fromPath || fromQuery || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (!code) return null
    try { localStorage.setItem('dzm_ref', code) } catch { /* private mode */ }
    // Strip only the ref (and the /invite/ path) — preserve other params like
    // ?verify= / ?reset= so their one-shot toasts/modals still fire.
    const url = new URL(window.location.href)
    url.searchParams.delete('ref')
    const path = url.pathname.startsWith('/invite/') ? '/' : url.pathname
    window.history.replaceState({}, '', path + url.search)
    return code
  })
  // Live ref so the consent-deferred prompt below sees the CURRENT auth state,
  // not whatever was captured when the listener was registered (you can log in
  // between opening an invite link and dismissing the consent banner).
  const authUserRef = useRef(authUser)
  authUserRef.current = authUser
  useEffect(() => {
    if (!pendingInvite || authLoading) return
    setPendingInvite(null)
    if (authUser) return                              // logged in → code waits in localStorage
    // Logged out → prompt sign-up. But don't stack the modal on the first-visit
    // consent banner: HeroUI makes the background inert, so the banner becomes
    // unclickable. Wait for the consent choice, then prompt — but only if still
    // logged out by then.
    const prompt = () => { if (!authUserRef.current) setAuthModal({ mode: 'signup' }) }
    if (analyticsConfigured() && getConsent() === null) {
      const onResolved = () => { window.removeEventListener('dzm-consent-resolved', onResolved); prompt() }
      window.addEventListener('dzm-consent-resolved', onResolved)
    } else {
      prompt()
    }
  }, [pendingInvite, authUser, authLoading])
  const [verifyMsg, setVerifyMsg] = useState<string | null>(() => {
    const v = new URLSearchParams(window.location.search).get('verify')
    if (v === 'ok') return 'Your email is confirmed. Welcome aboard.'
    if (v === 'invalid') return 'That confirmation link is invalid or has expired.'
    return null
  })
  // Entry points from the server-rendered pages (blog, /terms, /privacy, /bot).
  // Those headers are plain HTML and cannot open a React modal, so their
  // Settings and Sign in controls link to /?settings=1 and /?auth=1 and we
  // open the matching panel on mount. Keeps one header design across the site
  // instead of a reduced one on every non-SPA page.
  // The flag is read once (the effect below strips it from the URL), and the
  // sign-in modal waits for /api/auth/me: a signed-in visitor coming from a
  // doc page must not be shown a sign-in form.
  const pendingAuth = useRef(new URLSearchParams(window.location.search).get('auth') === '1')
  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    if (q.get('settings') === '1') setShowSettings(true)
    if (authLoading) return
    if (pendingAuth.current) { pendingAuth.current = false; if (!authUser) setAuthModal({ mode: 'signin' }) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading])

  // Strip one-shot auth query params (?reset=/?verify=/?auth=/?settings=) once read.
  useEffect(() => {
    if (/[?&](reset|verify|auth|settings)=/.test(window.location.search)) {
      const u = new URL(window.location.href)
      u.searchParams.delete('reset'); u.searchParams.delete('verify')
      u.searchParams.delete('auth'); u.searchParams.delete('settings')
      window.history.replaceState({}, '', u.pathname + u.search || '/')
    }
  }, [])
  useEffect(() => { if (verifyMsg) { const t = setTimeout(() => setVerifyMsg(null), 5000); return () => clearTimeout(t) } }, [verifyMsg])

  // Region (for Trending / Upcoming). IP-based detection is what comes back in
  // opts.you, but it can be wrong for VPN/proxy users — so we keep a user-set
  // override in localStorage. `regionOverride === undefined` = follow opts.you,
  // `null` = explicitly "Anywhere", string = explicit country code.
  const [regionOverride, setRegionOverrideRaw] = useState<string | null | undefined>(() => {
    try {
      const v = localStorage.getItem('dzm_region')
      if (v === null) return undefined         // never picked → auto-detect
      return v === '' ? null : v               // empty string sentinel = "Anywhere"
    } catch { return undefined }
  })
  const setRegionOverride = useCallback((cc: string | null) => {
    setRegionOverrideRaw(cc)
    try { localStorage.setItem('dzm_region', cc === null ? '' : cc) } catch { /* quota / private mode */ }
    trackEvent('region_set', '', cc ?? 'anywhere')
  }, [])
  // Effective region: explicit override wins, otherwise fall back to IP-detected.
  const effectiveRegion: string | null = regionOverride !== undefined ? regionOverride : (opts?.you ?? null)
  // Viewer country anchor for the "ping for you" estimate. This is the user's
  // PHYSICAL location (IP GeoIP), not the browsing region — so the estimate stays
  // "ping from where you are" even when they browse another region's servers. Falls
  // back to an explicit single-country override if GeoIP didn't resolve.
  const viewerCC: string | null = opts?.you ?? (regionOverride && regionOverride.length === 2 ? regionOverride : null)
  // Distance-based ping estimate for the browser's ping column, memoized per
  // country so a favourite toggle doesn't recompute it for every row; only the
  // viewer's location invalidates it. (A browser can't measure real ping; the
  // launcher passes a measured one instead.)
  const pingFor = useMemo(() => {
    const cache = new Map<string, number | null>()
    return (s: ServerRow) => {
      const key = s.country ?? ''
      if (!cache.has(key)) cache.set(key, estimatePingMs(viewerCC, s.country))
      return cache.get(key)!
    }
  }, [viewerCC])

  const [highlight, setHighlight] = useState<string[]>([])
  const openServer = useCallback((id: number, hl?: string[]) => {
    setSel(id)
    setHighlight(hl ?? [])
    window.history.pushState({ id }, '', `/server/${id}`)
    trackPageview()
  }, [])
  const closeServer = useCallback(() => {
    setSel(null)
    if (window.location.pathname.startsWith('/server/')) window.history.pushState({}, '', '/')
  }, [])
  const scrollToBrowser = useCallback(() => {
    const input = document.querySelector('input[placeholder^="e.g. vanilla"]') as HTMLInputElement | null
    if (!input) return
    input.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => input.focus({ preventScroll: true }), 500)
  }, [])
  const openDiscover = useCallback((tab: 'trending' | 'upcoming') => {
    setDiscoverState(tab)
    window.history.pushState({ discover: tab }, '', `/${tab}`)
    window.scrollTo(0, 0)
    trackPageview()
  }, [])
  const closeDiscover = useCallback(() => {
    setDiscoverState(null)
    if (['/trending', '/upcoming'].includes(window.location.pathname)) {
      window.history.pushState({}, '', '/')
    }
  }, [])
  const setDiscover = useCallback((tab: 'trending' | 'upcoming') => {
    setDiscoverState(tab)
    if (window.location.pathname !== `/${tab}`) {
      window.history.replaceState({ discover: tab }, '', `/${tab}`)
    }
  }, [])
  useEffect(() => {
    const onPop = () => {
      const p = window.location.pathname
      const m = p.match(/^\/server\/(\d+)/)
      setSel(m ? Number(m[1]) : null)
      if (p === '/trending') setDiscoverState('trending')
      else if (p === '/upcoming') setDiscoverState('upcoming')
      else setDiscoverState(null)
      setProfileOpen(p === '/profile')
      setSettingsPageOpen(p === '/profile/settings')
      const mu = p.match(/^\/u\/([^/]+)/)
      setPublicHandle(mu ? dec(mu[1]) : null)
      // Keep the URL-driven server-browser filter in sync with browser back/fwd
      // so /mod/X → / → /mod/X navigates correctly.
      setUrlFilter(parseUrlFilter(p))
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  // Clear the URL filter and go back to the plain homepage. Used by the
  // "clear" button on the contextual banner inside ServerBrowser.
  const clearUrlFilter = useCallback(() => {
    setUrlFilter(null)
    window.history.pushState({}, '', '/')
  }, [])

  // Keep the previous object when the payload hasn't changed so the 15s poll
  // doesn't re-render the page for nothing (memoized children stay skipped).
  const refresh = useCallback(() => {
    api.overview().then((next) => {
      setOv((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
    }).catch(() => {})
  }, [])
  // Poll the overview, but pause while the tab is hidden (no point hammering the
  // API + reconciling for a backgrounded tab); refresh once when it returns.
  useEffect(() => {
    refresh()
    let i: ReturnType<typeof setInterval> | undefined
    const start = () => { if (i === undefined) i = setInterval(refresh, 15000) }
    const stop = () => { if (i !== undefined) { clearInterval(i); i = undefined } }
    const onVis = () => { if (document.hidden) stop(); else { refresh(); start() } }
    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVis)
    return () => { stop(); document.removeEventListener('visibilitychange', onVis) }
  }, [refresh])
  useEffect(() => { api.filters().then(setOpts).catch(() => {}) }, [])
  // First-party analytics is consent-gated like GA4 — don't beacon or mint the
  // persistent id until the visitor has actively granted consent.
  useEffect(() => {
    let started = false
    const start = () => { if (!started && getConsent() === 'granted') { started = true; initTracking() } }
    start()
    window.addEventListener('dzm-consent-resolved', start)
    return () => window.removeEventListener('dzm-consent-resolved', start)
  }, [])
  // Stable ref so the memo'd TrendingStrip isn't re-rendered every 15s overview poll
  // by a fresh `[]`/array literal.
  const countries = useMemo(() => opts?.countries ?? [], [opts])

  return (
    <>
      <TopBar
        scrollToBrowser={scrollToBrowser}
        openSettings={() => setShowSettings(true)}
        openAuth={openAuth}
        openProfile={openProfile} />

      {authModal && (
        <AuthModal auth={authApi} onSignedIn={refreshAuth} initialMode={authModal.mode} resetToken={authModal.token}
          refCode={readRef()} providerHref={(p) => authApi.loginUrl(p, window.location.pathname + window.location.search)}
          onClose={() => setAuthModal(null)} />
      )}
      {pendingMerge.length > 0 && (
        <FavMergePrompt count={pendingMerge.length} onAdd={acceptMerge} onDecline={declineMerge} />
      )}
      {verifyMsg && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] rounded-xl border border-[var(--border)]
          bg-[var(--background-secondary)] px-4 py-3 text-sm shadow-xl max-w-[90vw]">
          {verifyMsg}
        </div>
      )}

      {/* Public profile /u/<handle> — no auth, anyone can view. */}
      {publicHandle ? (
        <Suspense fallback={<div className="max-w-[860px] mx-auto px-5 py-10 text-[var(--muted)]">Loading…</div>}>
          <PublicProfile handle={publicHandle} onHome={closePublic} />
        </Suspense>
      ) : /* /profile + /profile/settings replace the home content (auth-gated). */
      settingsPageOpen ? (
        <Suspense fallback={<div className="max-w-[860px] mx-auto px-5 py-10 text-[var(--muted)]">Loading…</div>}>
          <SettingsPage onBack={openProfile} />
        </Suspense>
      ) : profileOpen ? (
        <Suspense fallback={<div className="max-w-[860px] mx-auto px-5 py-10 text-[var(--muted)]">Loading…</div>}>
          <ProfilePageV2 onHome={closeProfile} onSettings={openSettingsPage} />
        </Suspense>
      ) : discover ? (
        <DiscoverPage tab={discover} setTab={setDiscover} onOpen={openServer} onClose={closeDiscover}
          region={effectiveRegion} you={opts?.you ?? null} viewerCC={viewerCC}
          countries={countries}
          onRegionChange={setRegionOverride} />
      ) : (
      <main className="max-w-[1320px] mx-auto px-5 py-6">
        {/* Beta notice. dayzmetrics.com is still under heavy development — we add
            features, swap layouts and fix bugs frequently. This strip is meant
            to set the right expectation without nagging: always visible, low-
            contrast, no dismiss button (we'll remove it ourselves when the site
            graduates from beta). */}
        <BetaNotice />

        {/* Hero banner — admin-editable image + copy with a graceful fallback to
            the original hero text when no banner is published. Same vertical air
            above and below so the page rhythm is unchanged. */}
        <HeroCarousel api={api} serversOnline={ov?.online} />
        {/* Everything below the hero stays visibility:hidden until the initial
            data batch settles, then appears in one commit. Before this, the
            trending/featured strips and the row table each popped in above
            already-painted content, shifting the whole page three times per
            load (lab CLS ~0.08–0.12, field 0.16). Content appearing into empty
            space moves nothing, so CLS stays clean; the failsafe timer in
            useBooted guarantees the page can never stay hidden. */}
        <div style={booted ? undefined : { visibility: 'hidden' }}>
        {/* Trending strip: hot servers right now, quality-gated and personalised by
            distance. Lives above the browser so it's the first thing visitors see. */}
        <TrendingStrip api={api} estimatePing={estimatePingMs} onOpen={openServer} onViewAll={openDiscover}
          region={effectiveRegion} you={opts?.you ?? null} viewerCC={viewerCC}
          countries={countries}
          onRegionChange={setRegionOverride} />

        {/* server browser — the main event, so it leads the page */}
        <div id="server-browser-anchor" />
        <SectionHead icon={<ServerIcon size={32} />} title="Server browser" />
        {!authUser && favs.length > 0 && (
          <FavMergeNudge count={favs.length} onSignIn={openAuth} />
        )}
        <FeaturedStrip favs={favs} onToggleFav={toggleFav} onOpen={openServer} api={api} />
        <ServerBrowser opts={opts} onOpen={openServer} favs={favs} onToggleFav={toggleFav}
          api={api} track={trackEvent} pingFor={pingFor}
          urlFilter={urlFilter} onClearUrlFilter={clearUrlFilter} />

        {/* ecosystem snapshot — live overview + trend, below the browser */}
        <SectionHead icon={<Activity size={32} />} title="Live overview" />
        {/* NOTE: the Steam-wide "DayZ worldwide" count is still collected hourly
            (dayz_player_count) and exposed on /api/overview (dayz_global) — it's
            just not shown here: next to our fake-filtered "Players online" it
            read as a contradiction. Data stays for blog posts / future use. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
          <Kpi label="Players online" value={fmt(ov?.players)} hero onClick={() => setMetric('players')} active={metric === 'players'} sub="real players · live" />
          <Kpi label="Servers online" value={fmt(ov?.online)} hero onClick={() => setMetric('servers')} active={metric === 'servers'} sub={`${fmt(ov?.suspect_online)} fakes filtered`} />
          <Kpi label="Servers tracked" value={fmt(ov?.servers_tracked)} sub="all-time · incl. offline" />
          <Kpi label="Updated" value={ov?.last_sweep ? new Date(ov.last_sweep).toLocaleTimeString([], { timeZone: tz }) : '—'} sub="last sweep" />
        </div>
        <Card className="mt-4">
          <Card.Header className="flex-row items-center justify-between">
            <Card.Title className="text-sm">{METRIC_LABEL[metric]} <span className="text-[var(--muted)] font-normal">· over time · click a card to switch</span></Card.Title>
            <RangeTabs value={range} onChange={setRange} />
          </Card.Header>
          <Card.Content>
            <Suspense fallback={<div className="h-[260px] flex items-center justify-center"><Spinner size="sm" /></div>}>
              <PopulationChart metric={metric} range={range} tz={tz} />
            </Suspense>
          </Card.Content>
        </Card>

        {BLOG_NAV_ENABLED && <BlogPanel api={api} />}

        {/* insights */}
        <SectionHead icon={<Database size={32} />} title="Maps & mods" />
        <Suspense fallback={<div className="h-40 flex items-center justify-center"><Spinner size="sm" /></div>}>
          <Insights />
        </Suspense>

        {/* Long-form SEO copy: a letter from the makers. Lives at the very
            bottom of the homepage so it's not in the way for the players who
            land here, but it gives crawlers ~500 words of original DayZ-themed
            content, including the H1 keywords ("find a DayZ server worth
            playing"), maps, modes and feature names. Decorated with a few
            transparent item PNGs from the inventory minigame to break up the
            text and feel like a dossier page, not a wall of marketing copy. */}
        <Manifesto api={api} track={trackEvent} />

        {/* Footer's own mt-12 was sized for the old layout; the manifesto now
            owns ~140px of bottom margin, so we trim back to mt-4 here to
            avoid stacking 200px of empty space below the letter. */}
        <SiteFooter lastSweep={ov?.last_sweep ?? null} />
        </div>
      </main>
      )}

      {sel != null && (
        <Suspense fallback={null}>
          <ServerModal id={sel} onClose={closeServer} onOpen={openServer} favs={favs} onToggleFav={toggleFav} tz={tz} viewerCC={viewerCC} highlightMods={highlight} onRequireAuth={openAuth} />
        </Suspense>
      )}
      {showSettings && <SettingsModal tz={tz} setTz={setTz} onClose={() => setShowSettings(false)} />}
    </>
  )
}
